import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { DomSanitizer } from "@angular/platform-browser";
import { debounceTime } from "rxjs/operators";
import { ReportDesignItemConfig } from "src/app/modules/bootstrap/modules/report/report-design-item";
import { buildTemplateWithDummyData } from "src/app/modules/bootstrap/modules/report/report-support";
import { HealthRecord } from "src/app/backend/models/health-record";
import { normalizeMemberIds } from "src/app/modules/bootstrap/modules/report/report-signature-support";
import { PrintService } from "src/app/services/print/print.service";
import { ReportService } from "src/app/services/report.service";
import { reportError } from "src/app/support/error-reporting";

const DUMMY_PDF_HEADER_HTML = `
<div style="background-color: #eff6ff; border-bottom: 2px solid #e2e8f0; padding: 8px 16px; text-align: center; margin-bottom: 8px;">
  <div style="font-size: 14px; font-weight: 500; color: #0f172a;">Esempio referto</div>
  <div style="font-size: 12px; color: #94a3b8;">
    <span>#2024-001</span>
    <span style="margin-left: 8px;">24/02/2025 10:30</span>
    <span style="margin-left: 8px;">12 kg</span>
  </div>
  <div style="font-size: 12px; color: #475569;">Dr. Mario Rossi</div>
</div>
<div style="display: grid; grid-template-columns: repeat(3, 1fr); font-size: 12px; gap: 4px 8px; margin-bottom: 16px;">
  <div style="display: flex; align-items: center; justify-content: space-between;"><span style="color: #64748b;">Paziente</span><span style="font-weight: 600; color: #0f172a;">Fido</span></div>
  <div style="display: flex; align-items: center; justify-content: space-between;"><span style="color: #64748b;">Specie</span><span style="font-weight: 600; color: #0f172a;">Cane</span></div>
  <div style="display: flex; align-items: center; justify-content: space-between;"><span style="color: #64748b;">Razza</span><span style="font-weight: 600; color: #0f172a;">Labrador</span></div>
  <div style="display: flex; align-items: center; justify-content: space-between;"><span style="color: #64748b;">Data di nascita</span><span style="font-weight: 600; color: #0f172a;">15/03/2018</span></div>
  <div style="display: flex; align-items: center; justify-content: space-between;"><span style="color: #64748b;">Sesso</span><span style="font-weight: 600; color: #0f172a;">Maschio</span></div>
  <div style="display: flex; align-items: center; justify-content: space-between;"><span style="color: #64748b;">Microchip</span><span style="font-weight: 600; color: #0f172a;">123456789012345</span></div>
  <div style="display: flex; align-items: center; justify-content: space-between;"><span style="color: #64748b;">Cliente</span><span style="font-weight: 600; color: #0f172a;">Giuseppe Verdi</span></div>
  <div style="display: flex; align-items: center; justify-content: space-between;"><span style="color: #64748b;">Telefono</span><span style="font-weight: 600; color: #0f172a;">+39 333 1234567</span></div>
  <div style="display: flex; align-items: center; justify-content: space-between;"><span style="color: #64748b;">Email</span><span style="font-weight: 600; color: #0f172a;">giuseppe.verdi&#64;example.com</span></div>
</div>
`;

const PREVIEW_DEBOUNCE_MS: number = 1000;

@Component({
  selector: "snt-report-designer-document-preview",
  templateUrl: "report-designer-document-preview.component.html",
  styles: `
    :host {
      display: flex;
      flex: 1;
      min-height: 0;
    }
  `,
  standalone: false
})
export class ReportDesignerDocumentPreviewComponent {
  readonly templateConfig = input<ReportDesignItemConfig[]>([]);
  readonly previewType = input.required<"soap" | "pdf">();

  readonly debouncedTemplateConfig = toSignal(
    toObservable(this.templateConfig).pipe(debounceTime(PREVIEW_DEBOUNCE_MS)),
    { initialValue: [] as ReportDesignItemConfig[] }
  );

  readonly pdfBlobUrl = signal<string | undefined>(undefined);
  readonly pdfLoading = signal(false);
  readonly pdfError = signal(false);
  readonly pdfOutdated = signal(false);

  readonly dummyPreviewData = computed(() =>
    buildTemplateWithDummyData(this.debouncedTemplateConfig() ?? [])
  );

  readonly previewHealthRecord = computed((): HealthRecord | undefined => {
    const authorIds = normalizeMemberIds(
      this.dummyPreviewData().dataSource.authors ??
        this.dummyPreviewData().dataSource.staff
    );

    if (authorIds.length === 0) {
      return undefined;
    }

    return {
      staff: authorIds.map((id) => ({
        id,
        number: "",
        firstName: "",
        lastName: ""
      }))
    } as HealthRecord;
  });

  readonly pdfBlobUrlSafe = computed(() => {
    const url = this.pdfBlobUrl();
    return url
      ? this.#domSanitizer.bypassSecurityTrustResourceUrl(url)
      : undefined;
  });

  readonly #reportService = inject(ReportService);
  readonly #domSanitizer = inject(DomSanitizer);
  readonly #printService = inject(PrintService);
  readonly #destroyRef = inject(DestroyRef);
  #previousTemplateConfigRef: ReportDesignItemConfig[] | undefined = undefined;

  constructor() {
    this.#destroyRef.onDestroy(() => this.#revokeBlobUrl());

    effect(() => {
      const current = this.debouncedTemplateConfig();
      const prev = this.#previousTemplateConfigRef;
      this.#previousTemplateConfigRef = current;

      if (
        prev !== undefined &&
        prev !== current &&
        this.previewType() === "pdf" &&
        this.pdfBlobUrl() !== undefined
      ) {
        this.pdfOutdated.set(true);
      }
    });

    effect(() => {
      const type = this.previewType();

      if (
        type === "pdf" &&
        this.pdfBlobUrl() === undefined &&
        !this.pdfLoading()
      ) {
        this.refreshPdf();
      }
    });
  }

  async refreshPdf(): Promise<void> {
    const { template, dataSource } = this.dummyPreviewData();
    this.pdfLoading.set(true);
    this.pdfError.set(false);

    try {
      const result = await this.#reportService.templateToHTML(
        template,
        dataSource,
        {
          renderer: "data",
          componentsRenderers: [],
          headerTitle: "",
          preview: true,
          onlyTextWithValue: false,
          useFakeTextData: true
        }
      );

      const contentWithDummyHeader = DUMMY_PDF_HEADER_HTML + result.content;

      const pdfBlob =
        await this.#printService.htmlToPdfNoHeaderFooterAndMargins(
          contentWithDummyHeader
        );

      this.#revokeBlobUrl();

      const blobUrl = URL.createObjectURL(pdfBlob);
      this.pdfBlobUrl.set(blobUrl);
      this.pdfOutdated.set(false);
    } catch (ex) {
      reportError(ex);
      this.pdfError.set(true);
    } finally {
      this.pdfLoading.set(false);
    }
  }

  #revokeBlobUrl(): void {
    const url = this.pdfBlobUrl();
    if (url) {
      URL.revokeObjectURL(url);
      this.pdfBlobUrl.set(undefined);
    }
  }
}
